import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { differenceInDays } from 'date-fns';
import { getDealSalesAmount } from '@/utils/salesAmount';

const STAGE_CONFIG = {
  new: { label: 'Novo', color: 'hsl(var(--chart-1))' },
  contact: { label: 'Contato', color: 'hsl(var(--chart-2))' },
  scheduled: { label: 'Reunião Agendada', color: 'hsl(var(--chart-3))' },
  proposal: { label: 'Proposta', color: 'hsl(var(--chart-4))' },
  negotiation: { label: 'Negociação', color: 'hsl(var(--chart-5))' },
};

// Color for days badge based on age
const getDaysColor = (days) => {
  if (days >= 90) return 'bg-destructive text-destructive-foreground';
  if (days >= 60) return 'bg-orange-500 text-white';
  if (days >= 30) return 'bg-amber-500 text-white';
  if (days >= 14) return 'bg-yellow-500 text-black';
  return 'bg-muted text-muted-foreground';
};

export default function DealAgingByCreationList({ 
  deals = [], 
  users = [],
  loading = false 
}) {
  // Process deals to add days since creation
  const dealsWithAge = useMemo(() => {
    const now = new Date();
    
    return deals
      .filter(d => d.stage !== 'won' && d.stage !== 'lost')
      .map(deal => ({
        ...deal,
        days_since_creation: differenceInDays(now, new Date(deal.created_at))
      }))
      .sort((a, b) => b.days_since_creation - a.days_since_creation);
  }, [deals]);

  const handleDealClick = (dealId) => {
    window.open(`/DealDetail?id=${dealId}`, '_blank');
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getUserName = (userId) => {
    if (!userId) return null;
    const user = users.find(u => u.id === userId);
    return user?.display_name?.split(' ')[0] || null;
  };

  if (loading) {
    return (
      <div className="h-[300px] flex items-center justify-center text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (dealsWithAge.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center text-muted-foreground">
        No active deals
      </div>
    );
  }

  return (
    <ScrollArea className="h-[300px]">
      <div className="space-y-2 pr-4">
        {dealsWithAge.map((deal) => {
          const stageConfig = STAGE_CONFIG[deal.stage] || { label: deal.stage, color: 'bg-muted' };
          const contactName = deal.contact 
            ? `${deal.contact.first_name || ''} ${deal.contact.last_name || ''}`.trim() || 'Unknown'
            : 'Unknown';
          const consultantName = getUserName(deal.sales_consultant);
          const sdrName = getUserName(deal.sales_development);
          // Usar valor efetivo (regra de cancelamento)
          const amount = getDealSalesAmount({
            deal_status: deal.deal_status,
            closed_date: deal.closed_date,
            cancelled_at: deal.cancelled_at,
            total_amount: deal.total_amount || deal.amount,
            amount_received: deal.amount_received || 0,
          });

          return (
            <div
              key={deal.id}
              onClick={() => handleDealClick(deal.id)}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors border border-transparent hover:border-border"
            >
              {/* Days badge */}
              <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex flex-col items-center justify-center ${getDaysColor(deal.days_since_creation)}`}>
                <span className="text-lg font-bold leading-none">{deal.days_since_creation}</span>
                <span className="text-[10px] leading-none">days</span>
              </div>

              {/* Deal info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm truncate">{contactName}</span>
                  <Badge 
                    variant="outline" 
                    className="text-xs flex-shrink-0"
                    style={{ 
                      borderColor: stageConfig.color,
                      color: stageConfig.color,
                    }}
                  >
                    {stageConfig.label}
                  </Badge>
                </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                  <span>Consultant: {consultantName || '-'}</span>
                  <span>• SDR: {sdrName || '-'}</span>
                </div>
              </div>

              {/* Amount */}
              <div className="flex-shrink-0 text-right">
                <span className="text-sm font-medium">{formatCurrency(amount)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
